import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { reports } from "../../api/endpoints";
import ModelBadge from "../../components/ModelBadge.jsx";
import { useSelectedWebsite } from "./Layout.jsx";

export default function Sources() {
  const { t } = useTranslation();
  const { websiteId } = useSelectedWebsite();
  const [data, setData] = useState({ own_pages: [], other_sources: [] });
  const [tab, setTab] = useState("own");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!websiteId) return;
    setLoading(true);
    reports
      .sources(websiteId)
      .then((r) => setData(r.data || { own_pages: [], other_sources: [] }))
      .finally(() => setLoading(false));
  }, [websiteId]);

  if (!websiteId)
    return <div className="text-slate-500">{t("prompts.pickWebsite")}</div>;

  const items = tab === "own" ? data.own_pages : data.other_sources;
  const emptyLabel = tab === "own" ? t("sources.noOwnPages") : t("sources.noOtherSources");

  // Per-platform citation totals across every own page — the KPI strip
  // below mirrors a "Google AI Overviews / ChatGPT / ... / Total" card
  // row, computed live from our own scan data rather than a separate
  // paid feed.
  const platformTotals = {};
  let grandTotal = 0;
  for (const page of data.own_pages) {
    for (const [model, count] of Object.entries(page.by_model || {})) {
      platformTotals[model] = (platformTotals[model] || 0) + count;
      grandTotal += count;
    }
  }
  const platformEntries = Object.entries(platformTotals).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">{t("sources.title")}</h1>
        <p className="text-sm text-slate-600">{t("sources.subtitle")}</p>
      </div>

      {grandTotal > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {platformEntries.map(([model, count]) => (
            <div
              key={model}
              className="rounded-2xl bg-white border border-slate-200/70 shadow-sm p-4"
            >
              <ModelBadge modelKey={model} size="sm" />
              <div className="mono text-2xl font-bold text-slate-900 mt-2">{count}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">
                {t("sources.colPlatforms")}
              </div>
            </div>
          ))}
          <div className="rounded-2xl bg-brand-50 border border-brand-200 shadow-sm p-4">
            <div className="text-xs font-medium text-brand-700">{t("sources.totalCitations")}</div>
            <div className="mono text-2xl font-bold text-brand-800 mt-2">{grandTotal}</div>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <TabButton active={tab === "own"} onClick={() => setTab("own")}>
          {t("sources.ownPagesTab")} ({data.own_pages.length})
        </TabButton>
        <TabButton active={tab === "other"} onClick={() => setTab("other")}>
          {t("sources.otherSourcesTab")} ({data.other_sources.length})
        </TabButton>
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("sources.colUrl")}</th>
              <th className="px-4 py-3 font-medium w-20">
                {t("sources.colFrequency")}
              </th>
              {tab === "own" && (
                <th className="px-4 py-3 font-medium w-56">
                  {t("sources.colPlatforms")}
                </th>
              )}
              <th className="px-4 py-3 font-medium w-40">
                {t("sources.colLastSeen")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td colSpan={tab === "own" ? 4 : 3} className="px-4 py-6 text-slate-500">
                  {t("sources.loading")}
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={tab === "own" ? 4 : 3} className="px-4 py-6 text-slate-500">
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              items.map((s) => (
                <tr key={s.url}>
                  <td className="px-4 py-3">
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand-700 hover:underline break-all"
                    >
                      {s.url}
                    </a>
                  </td>
                  <td className="px-4 py-3 mono">{s.frequency}</td>
                  {tab === "own" && (
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(s.by_model || {}).map(([model, count]) => (
                          <span key={model} className="inline-flex items-center gap-1">
                            <ModelBadge modelKey={model} size="sm" />
                            <span className="text-xs text-slate-400 mono">×{count}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                  )}
                  <td className="px-4 py-3 text-slate-500">
                    {new Date(s.last_seen).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm font-medium ring-1 transition ${
        active
          ? "bg-brand-600 text-white ring-brand-600"
          : "bg-white text-slate-600 ring-slate-300 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}
